import type {
  MedicalProfileSummary,
  PublicLookupData,
  RegistrationSummary,
  TagType,
  VehicleSummary,
} from "@/lib/api/client"

export interface PublicScanSearch {
  card?: string
  code?: string
  device?: string
  qr?: string
  tag?: string
  vehicle?: string
}

export type PublicScanDestination = "register" | "report"
export type PublicActivationState = "activated" | "registration_required"

const PUBLIC_SCAN_KEYS = [
  "tag",
  "device",
  "card",
  "qr",
  "code",
  "vehicle",
] as const

export function normalizeTagType(value: unknown): TagType | null {
  if (value !== "vehicle_sticker" && value !== "personal_tag" && value !== "fleet_tag") {
    return null
  }

  return value
}

export function resolveScanTag(search: PublicScanSearch): string | null {
  for (const key of PUBLIC_SCAN_KEYS) {
    const value = search[key]

    if (typeof value === "string" && value.trim()) {
      return value.trim()
    }
  }

  return null
}

export function getVehicleLabel(vehicle: VehicleSummary | null): string {
  if (!vehicle) {
    return ""
  }

  const pieces = [vehicle.color, vehicle.make, vehicle.model]
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean)

  if (pieces.length > 0) {
    return pieces.join(" ")
  }

  return [vehicle.make, vehicle.model].filter(Boolean).join(" ")
}

export function getMaskedPlate(vehicle: VehicleSummary | null): string {
  const value = vehicle?.plateNumber?.replace(/\s+/g, "") ?? ""

  if (!value) {
    return ""
  }

  return value.slice(-4)
}

export function getOrganizationIdFromLookup(
  lookup: PublicLookupData | null
): string | undefined {
  const registrationOrganizationId = lookup?.registration?.organizationId

  if (typeof registrationOrganizationId === "string" && registrationOrganizationId) {
    return registrationOrganizationId
  }

  const deviceOrganizationId = lookup?.device?.organizationId

  if (typeof deviceOrganizationId === "string" && deviceOrganizationId) {
    return deviceOrganizationId
  }

  return undefined
}

export function supportsVehicleContext(tagType: TagType | null): boolean {
  return tagType === "vehicle_sticker" || tagType === "fleet_tag"
}

export function getReportSubjectSummary(
  reportKind: PublicScanDestination | "behavior" | "incident",
  tagType: TagType | null
) {
  if (tagType === "personal_tag") {
    return {
      title: "Personal-tag reports stay person-centered",
      description:
        "The person linked to this tag stays the subject, while emergency context remains supporting metadata.",
    }
  }

  if (tagType === "vehicle_sticker" || tagType === "fleet_tag") {
    if (reportKind === "behavior") {
      return {
        title: "Behavior reports focus on the observed driver",
        description:
          "The linked vehicle remains context, but the unsafe behavior is attributed to the active driver or user.",
      }
    }

    return {
      title: "Incident reports stay vehicle-centered",
      description:
        "The linked vehicle remains the subject for responder routing and downstream incident handling.",
    }
  }

  if (reportKind === "behavior") {
    return {
      title: "Behavior reports are handled separately",
      description:
        "Unsafe conduct, blocking, or tampering can be reported even when the scanner cannot fully resolve the tag context.",
    }
  }

  return {
    title: "Incident reporting stays the default",
    description:
      "Use the incident path for crashes, roadside hazards, medical issues, and active scenes that need responder context.",
  }
}

export function buildRegistrationSeed(
  lookup: PublicLookupData | null,
  currentTagType: TagType
): RegistrationSummary | null {
  const registration = lookup?.registration ?? null

  if (registration) {
    return registration
  }

  if (!lookup) {
    return null
  }

  return {
    tag: lookup.tag,
    tagType: lookup.tagType ?? currentTagType,
    stickerId: lookup.tag,
    organizationId: lookup.device?.organizationId ?? null,
    ownerName: lookup.contact?.name ?? null,
    ownerEmail: lookup.contact?.email ?? null,
    ownerPhone: lookup.contact?.phone ?? null,
    emergencyContactName: lookup.contact?.emergencyContactName ?? null,
    emergencyContactPhone: lookup.contact?.emergencyContactPhone ?? null,
    vehicleType: lookup.vehicle?.type ?? null,
    vehicleMake: lookup.vehicle?.make ?? null,
    vehicleModel: lookup.vehicle?.model ?? null,
    vehicleYear: lookup.vehicle?.year ?? null,
    vehicleColor: lookup.vehicle?.color ?? null,
    vin: lookup.vehicle?.vin ?? null,
    plateNumber: lookup.vehicle?.plateNumber ?? null,
    bloodType: lookup.medicalProfile?.bloodType ?? null,
    allergies: lookup.medicalProfile?.allergies ?? null,
    medicalConditions: lookup.medicalProfile?.medicalConditions ?? null,
    medications: lookup.medicalProfile?.medications ?? null,
    responderNotes: lookup.medicalProfile?.responderNotes ?? null,
    consentAccepted: lookup.medicalProfile?.consentAccepted ?? null,
    status: lookup.device?.status ?? "active",
    createdAt: null,
    updatedAt: null,
  }
}

export function getMedicalConsentValue(
  medicalProfile: MedicalProfileSummary | null,
  registration: RegistrationSummary | null
) {
  return Boolean(medicalProfile?.consentAccepted ?? registration?.consentAccepted)
}
